/**
 * platform-account-page.tsx — Cài đặt cá nhân của super-admin (Platform).
 * Đổi họ tên hiển thị + đổi mật khẩu (đổi xong thu hồi mọi phiên khác, giữ phiên hiện tại).
 */
import { useEffect, useState } from 'react'
import { Loader2, Save, KeyRound } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/shared/page-header'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { platformApi, platformApiError, setPlatformTokens } from '@/lib/platform-client'
import { usePlatformAuthStore } from '@/stores/platform-auth-store'
import { PlatformTotpCard } from './platform-totp-card'

export function PlatformAccountPage() {
  const admin = usePlatformAuthStore((s) => s.admin)
  const setAdmin = usePlatformAuthStore((s) => s.setAdmin)
  const [fullName, setFullName] = useState(admin?.fullName ?? '')
  const [savingName, setSavingName] = useState(false)
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' })
  const [savingPw, setSavingPw] = useState(false)

  useEffect(() => { setFullName(admin?.fullName ?? '') }, [admin?.fullName])

  if (!admin) return null
  const nameChanged = fullName.trim() !== admin.fullName && fullName.trim().length >= 2

  const saveName = async () => {
    setSavingName(true)
    try {
      const { data } = await platformApi.patch('/platform/auth/me', { fullName: fullName.trim() })
      setAdmin(data)
      toast.success('Đã cập nhật họ tên')
    } catch (err) {
      toast.error(platformApiError(err))
    } finally {
      setSavingName(false)
    }
  }

  const pwError =
    pw.next && pw.next.length < 8 ? 'Mật khẩu mới tối thiểu 8 ký tự'
      : pw.confirm && pw.next !== pw.confirm ? 'Nhập lại mật khẩu mới chưa khớp'
        : ''
  const canSavePw = !!pw.current && pw.next.length >= 8 && pw.next === pw.confirm && !savingPw

  const savePassword = async () => {
    setSavingPw(true)
    try {
      const { data } = await platformApi.post('/platform/auth/change-password', {
        currentPassword: pw.current,
        newPassword: pw.next,
      })
      setPlatformTokens(data.token, data.refreshToken)
      setPw({ current: '', next: '', confirm: '' })
      toast.success('Đã đổi mật khẩu — các phiên đăng nhập khác đã bị đăng xuất')
    } catch (err) {
      toast.error(platformApiError(err))
    } finally {
      setSavingPw(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Cài đặt cá nhân" description="Thông tin tài khoản super-admin của bạn." />

      <Card>
        <CardHeader>
          <CardTitle>Thông tin tài khoản</CardTitle>
          <CardDescription>Email dùng để đăng nhập Platform Console.</CardDescription>
        </CardHeader>
        <CardContent className="grid max-w-xl gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="pa-email">Email</Label>
            <Input id="pa-email" value={admin.email} disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pa-name">Họ tên</Label>
            <Input id="pa-name" value={fullName} maxLength={100} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div>
            <Button onClick={saveName} disabled={!nameChanged || savingName}>
              {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lưu
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Đổi mật khẩu</CardTitle>
          <CardDescription>Đổi xong, mọi phiên đăng nhập khác của bạn bị đăng xuất; phiên hiện tại vẫn giữ.</CardDescription>
        </CardHeader>
        <CardContent className="grid max-w-xl gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="pa-cur">Mật khẩu hiện tại</Label>
            <Input id="pa-cur" type="password" autoComplete="current-password" value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pa-new">Mật khẩu mới</Label>
            <Input id="pa-new" type="password" autoComplete="new-password" value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pa-confirm">Nhập lại mật khẩu mới</Label>
            <Input id="pa-confirm" type="password" autoComplete="new-password" value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
          </div>
          {pwError && <p className="text-sm text-destructive">{pwError}</p>}
          <div>
            <Button onClick={savePassword} disabled={!canSavePw}>
              {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Đổi mật khẩu
            </Button>
          </div>
        </CardContent>
      </Card>

      <PlatformTotpCard />
    </div>
  )
}
